import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import type { Kopf } from '../komponenten/Rahmen';

import {
  BELEGARTNAME,
  STATUSNAME,
  ZAHLUNGSSTANDNAME,
  akte as holeAkte,
  festschreiben,
  leistungszeitraumSetzen,
  liste,
  loeschen,
  mahnungen as holeMahnungen,
  offenePosten as holeOffenePosten,
  positionLoeschen,
  stornieren,
  zahlungVermerken,
  type Navisionmahnung,
  type OffenerPosten,
  type Rechnung,
  type Rechnungsstatus,
  type Rechnungszeile,
  type Zahlungsstand,
  versandadresse,
} from '../api/rechnungen';
import { Belegabschnitt } from '../beleg/Belegabschnitt';
import { Angabenmaske } from './Angabenmaske';
import { Empfaengerwahl } from './Empfaengerwahl';
import { GutschriftZurRechnung } from './Gutschriftmaske';
import { ApiFehler } from '../api/fehler';
import { markenklasse, type Markenart } from '../marken';
import { alsEuro, alsMenge, alsProzent } from '../betrag';
import { alsTag, heute } from '../datum';
import { kurzTag } from '../kunde/kundenvorgaenge';
import {
  Aktenrumpf,
  Feldraster,
  Feldzeile,
  Kennzahlenleiste,
  Reiterleiste,
  Verlauf,
  type Verlaufseintrag,
} from '../komponenten/Akte';
import { Aktenverweis, Verbunden, type Aktenwege } from '../komponenten/Aktenwege';
import type { Filtermarke } from '../komponenten/Filtermarken';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { AbTabletHinweis } from '../komponenten/AbTablet';
import { useTelefon } from '../komponenten/telefon';
import { Rundungshinweis } from '../komponenten/Rundungshinweis';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { KundeVerweis } from '../komponenten/Kontaktkarte';
import { Guthabenhandlung, Guthabenverlauf } from './Guthabenhandlung';

export const STATUSMARKE: Readonly<Record<Rechnungsstatus, Markenart>> = {
  entwurf: 'stillgelegt',
  /* Festgeschrieben ist der Abschluss des Belegs: unveraenderlich (GoBD). */
  festgeschrieben: 'abschluss',
  storniert: 'sackgasse',
};

export const ZAHLUNGSMARKE: Readonly<Record<Zahlungsstand, Markenart>> = {
  /* Eine offene Rechnung ist der Normalfall, keine Stilllegung. */
  offen: 'laufend',
  teilbezahlt: 'laufend',
  bezahlt: 'abschluss',
  ueberfaellig: 'sackgasse',
  /*
   * AUSGEBUCHT IST ABGESCHLOSSEN, nicht alarmierend: Die Forderung ist
   * abgeschrieben, es ist nichts mehr zu tun. Rot stand hier neben
   * „ueberfaellig" — und nur eines von beiden verlangt eine Handlung.
   */
  ausgebucht: 'stillgelegt',
};

/**
 * Die Rechnungsliste (Lastenheft Kapitel 8).
 *
 * Seitentyp Liste (ÄA-41): der Zähler im Seitenkopf, darunter Filterleiste und
 * Rastertabelle — derselbe Baustein wie bei Angeboten und Aufträgen.
 *
 * DIE NUMMERNSPALTE IST BEI ENTWÜRFEN LEER, und das ist keine Lücke in der
 * Anzeige: Ein Entwurf hat keine Nummer (RNG-03). Sie entsteht mit der
 * Festschreibung — jeder verworfene Entwurf hätte sonst eine Lücke
 * hinterlassen, die niemand erklären kann (BP-16).
 */
export function Rechnungsliste({
  zurKunde,
  beiOeffnen,
}: {
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde?: ((id: string) => void) | undefined;
  readonly beiOeffnen: (id: string) => void;
}): JSX.Element {
  const [suche, setzeSuche] = useState('');
  const [status, setzeStatus] = useState<Rechnungsstatus | ''>('');
  const [zahlung, setzeZahlung] = useState<Zahlungsstand | ''>('');
  const [zeilen, setzeZeilen] = useState<readonly Rechnungszeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(
    async (s: string, st: Rechnungsstatus | '', z: Zahlungsstand | ''): Promise<void> => {
      setzeLaedt(true);
      try {
        setzeZeilen(await liste({ suche: s, status: st, zahlungsstand: z }));
        setzeFehler(undefined);
      } catch (ursache) {
        setzeFehler(ursache as ApiFehler);
      } finally {
        setzeLaedt(false);
      }
    },
    [],
  );

  useEffect(() => {
    const uhr = setTimeout(() => void laden(suche, status, zahlung), 250);
    return () => clearTimeout(uhr);
  }, [suche, status, zahlung, laden]);

  /*
   * Die aktiven Filter als abwaehlbare Marken (2.9). Die Suche steht NICHT
   * darunter: Sie ist im Feld sichtbar, und eine Marke daneben waere dasselbe
   * zweimal. Gezeigt wird, was die Liste verkuerzt, ohne dass man es sieht.
   */
  const marken: readonly Filtermarke[] = [
    ...(status !== ''
      ? [{ text: `Status: ${STATUSNAME[status]}`, entfernen: () => setzeStatus('') }]
      : []),
    ...(zahlung !== ''
      ? [
          {
            text: `Zahlung: ${ZAHLUNGSSTANDNAME[zahlung]}`,
            entfernen: () => setzeZahlung(''),
          },
        ]
      : []),
  ];

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. „1 Rechnungen" wäre ein Fehler, den jeder sieht. */
  useZaehler(laedt ? undefined : zaehlwort(zeilen.length, 'Rechnung', 'Rechnungen'));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Liste
        suche={{
          wert: suche,
          aendern: setzeSuche,
          beschriftung: 'Rechnungen suchen',
          platzhalter: 'Rechnungsnummer oder Betreff',
        }}
        filter={
          <>
            <select
              className="liste__filter"
              value={status}
              onChange={(e) => setzeStatus(e.target.value as Rechnungsstatus | '')}
              aria-label="Status"
            >
              <option value="">Alle Status</option>
              {(Object.keys(STATUSNAME) as Rechnungsstatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUSNAME[s]}
                </option>
              ))}
            </select>
            <select
              className="liste__filter"
              value={zahlung}
              onChange={(e) => setzeZahlung(e.target.value as Zahlungsstand | '')}
              aria-label="Zahlungsstand"
            >
              <option value="">Jeder Zahlungsstand</option>
              {(Object.keys(ZAHLUNGSSTANDNAME) as Zahlungsstand[]).map((s) => (
                <option key={s} value={s}>
                  {ZAHLUNGSSTANDNAME[s]}
                </option>
              ))}
            </select>
          </>
        }
        marken={marken}
        hinweise={
          /* AUF-19, RNG-06 */
          <p className="hilfe">
            Rechnungen entstehen aus bestätigten Aufträgen — auch als Sammelrechnung über
            mehrere Aufträge eines Kunden. Ein Entwurf trägt noch keine Nummer; sie entsteht
            mit der Festschreibung, damit die Reihe lückenlos bleibt.
          </p>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">
              {zeilen.length === 1 ? '1 Rechnung' : `${String(zeilen.length)} Rechnungen`}
            </caption>
            <thead>
              <tr>
                <th scope="col" className="liste__spalte--knapp">
                  Nummer
                </th>
                <th scope="col">Art</th>
                <th scope="col">Kunde</th>
                <th scope="col">Betreff</th>
                <th scope="col">fällig</th>
                <th scope="col">Status</th>
                <th scope="col">Zahlung</th>
                <th scope="col" className="zahl">
                  Brutto
                </th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((r) => (
                <tr key={r.id}>
                  <td className="liste__spalte--knapp">
                    <button type="button" className="liste__oeffnen liste__nummer" onClick={() => beiOeffnen(r.id)}>
                      {r.rechnungsnummer ?? '(Entwurf)'}
                    </button>
                  </td>
                  <td>{BELEGARTNAME[r.belegart]}</td>
                  <td className="liste__name">
                  <KundeVerweis kundeId={r.kunde} zurAkte={zurKunde}>
                    {r.kundenname}
                  </KundeVerweis>
                </td>
                  <td>{r.betreff}</td>
                  <td className="liste__einzeilig">{r.status === 'entwurf' ? '—' : (alsTag(r.faelligAm) ?? '—')}</td>
                  <td>
                    <span className={markenklasse(STATUSMARKE[r.status])}>
                      {STATUSNAME[r.status]}
                    </span>
                  </td>
                  <td>
                    {r.status === 'entwurf' ? (
                      <span className="feld__hilfe">—</span>
                    ) : (
                      <span className={markenklasse(ZAHLUNGSMARKE[r.zahlungsstand])}>
                        {ZAHLUNGSSTANDNAME[r.zahlungsstand]}
                      </span>
                    )}
                  </td>
                  <td className="zahl">{alsEuro(r.belegbrutto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && zeilen.length === 0 && (
          <div className="liste__leer" role="status">
            <p>
              {suche === '' && status === '' && zahlung === ''
                ? 'Noch keine Rechnung. Sie entsteht in der Auftragsakte — dort steht der Weg dorthin.'
                : `Kein Treffer${suche === '' ? '' : ` für „${suche}“`}.`}
            </p>
          </div>
        )}
      </Liste>
    </>
  );
}

/** Ein Eintrag des Verlaufs mit dem Tag, nach dem er einsortiert wird. */
type Datiert = Verlaufseintrag & { readonly wann: string };

/** Die Zahlungsstände, zu denen es einen offenen Posten gibt (RNG-24). */
const OFFEN: readonly Zahlungsstand[] = ['offen', 'teilbezahlt', 'ueberfaellig'];

/**
 * DER VERLAUF DER RECHNUNG (ÄA-41): Zahlungen und Mahnstand in der
 * Seitenleiste, aus dem, was das CRM schon weiß — Anlage, Festschreibung,
 * Zahlungsziel, Skontofrist, der Zahlungsvermerk (RNG-23), die Staffel des
 * offenen Postens (RNG-24) und die Korrekturbelege (RNG-05). Neuestes oben;
 * ein Ziel in der Zukunft steht deshalb über dem, was schon war.
 *
 * EINE ZAHLUNG, NICHT MEHRERE. Der Vermerk führt den bisher insgesamt
 * erhaltenen Betrag und den letzten Eingang; einzelne Eingänge kennt das CRM
 * nicht, bis sie aus Navision zurückfließen (FIB-08). Der Verlauf zeigt
 * deshalb genau einen Eintrag „erhalten".
 *
 * DIE FÄLLIGKEIT IST DIE STAFFEL VOM SERVER und wird hier nicht aus Tagen
 * errechnet — AUS-01: Zwei Auswertungen derselben Kennzahl ziehen dieselben
 * Grenzen.
 *
 * DIE MAHNUNGEN ERTEILT NAVISION (RNG-25, E-39). Bis Auftrag CRM-115 hieß
 * die Staffel hier „Mahnstand“; überfällig ist aber nicht gemahnt. Jetzt
 * steht die Staffel als Fälligkeit da, und jede Mahnstufe aus dem Rückfluss
 * steht als eigener Eintrag mit Datum und Gebühr.
 */
export function verlaufDerRechnung(
  r: Rechnung,
  posten: OffenerPosten | undefined,
  stichtag: string,
  beiRechnung: (id: string) => void,
  mahnungen: readonly Navisionmahnung[] = [],
): readonly Verlaufseintrag[] {
  const eintraege: Datiert[] = [
    {
      kennung: 'angelegt',
      art: 'still',
      sinnbild: 'file-text',
      titel: `${BELEGARTNAME[r.belegart]} als Entwurf angelegt`,
      detail: r.betreff,
      datum: kurzTag(r.angelegtAm),
      wann: r.angelegtAm.slice(0, 10),
    },
  ];

  if (r.bezugsbeleg !== null && r.bezugsnummer !== null) {
    const bezug = r.bezugsbeleg;
    eintraege.push({
      kennung: 'bezug',
      art: 'vorgang',
      sinnbild: 'receipt',
      titel: `Bezug auf ${r.bezugsnummer}`,
      detail: BELEGARTNAME[r.belegart],
      datum: kurzTag(r.belegdatum),
      wann: r.belegdatum,
      beiWahl: () => beiRechnung(bezug),
    });
  }

  if (r.festgeschriebenAm !== null) {
    eintraege.push({
      kennung: 'festgeschrieben',
      art: 'erledigt',
      sinnbild: 'check',
      titel: `Festgeschrieben als ${r.rechnungsnummer ?? '—'}`,
      detail: r.festgeschriebenVonName === null ? undefined : `von ${r.festgeschriebenVonName}`,
      datum: kurzTag(r.festgeschriebenAm),
      wann: r.festgeschriebenAm.slice(0, 10),
    });
  }

  if (r.status === 'festgeschrieben') {
    const ueberfaellig = r.zahlungsstand === 'ueberfaellig';
    eintraege.push({
      kennung: 'faellig',
      art: ueberfaellig ? 'achtung' : OFFEN.includes(r.zahlungsstand) ? 'vorgang' : 'still',
      sinnbild: 'hourglass-medium',
      titel: ueberfaellig ? 'Zahlungsziel überschritten' : 'Zahlungsziel',
      detail: `fällig am ${alsTag(r.faelligAm) ?? '—'} · ${r.zahlungsziel} Tage`,
      dringend: ueberfaellig,
      datum: kurzTag(r.faelligAm),
      wann: r.faelligAm,
    });

    if (r.skontoProzent !== null && r.skontoBis !== null) {
      eintraege.push({
        kennung: 'skonto',
        art: OFFEN.includes(r.zahlungsstand) ? 'vorgang' : 'still',
        sinnbild: 'currency-eur',
        titel: `Skonto ${alsProzent(r.skontoProzent)}`,
        detail: `bei Zahlung bis ${alsTag(r.skontoBis) ?? '—'}`,
        datum: kurzTag(r.skontoBis),
        wann: r.skontoBis,
      });
    }
  }

  /*
   * BP-31, Auftrag CRM-113: Storno und Gutschrift gleichen die Rechnung aus.
   * Ist mehr eingegangen als geschuldet, steht ein Guthaben bis zur Erstattung.
   */
  if (posten !== undefined && posten.art === 'guthaben') {
    eintraege.push({
      kennung: 'guthaben',
      art: 'achtung',
      sinnbild: 'currency-eur',
      titel: `Guthaben ${alsEuro(posten.offenerBetrag.replace('-', ''))}, Erstattung oder Verrechnung offen`,
      detail:
        posten.ausgleich === null
          ? 'Gutschrift ohne Bezug'
          : `ausgeglichen durch ${posten.ausgleich}`,
      dringend: true,
      datum: kurzTag(stichtag),
      wann: stichtag,
    });
  }

  /* RNG-24: die Staffel des offenen Postens, zum Stichtag heute. */
  if (posten !== undefined && posten.art === 'forderung') {
    const faellig = posten.staffel !== 'nicht faellig';
    eintraege.push({
      kennung: 'faelligkeit',
      art: faellig ? 'achtung' : 'vorgang',
      sinnbild: 'warning-circle',
      titel: `Fälligkeit: ${posten.staffel}`,
      detail:
        posten.tageUeberfaellig > 0
          ? `${posten.tageUeberfaellig} ${posten.tageUeberfaellig === 1 ? 'Tag' : 'Tage'} überfällig`
          : 'offen, noch im Zahlungsziel',
      dringend: faellig,
      datum: kurzTag(stichtag),
      wann: stichtag,
    });
  }

  /* RNG-25: jede Mahnstufe, die Navision gemeldet hat. */
  for (const m of mahnungen) {
    eintraege.push({
      kennung: `mahnung-${m.mahnstufe}`,
      art: 'achtung',
      sinnbild: 'envelope-simple',
      titel: `${m.mahnstufe}. Mahnung (Navision)`,
      detail:
        Number(m.mahngebuehr) === 0 ? 'ohne Mahngebühr' : `Mahngebühr ${alsEuro(m.mahngebuehr)}`,
      datum: kurzTag(m.mahndatum),
      wann: m.mahndatum,
    });
  }

  /* RNG-23, C18.4: der vermerkte Eingang — Summe und letztes Eingangsdatum. */
  if (Number(r.bezahlterBetrag) !== 0) {
    eintraege.push({
      kennung: 'zahlung',
      art: r.zahlungsstand === 'bezahlt' ? 'erledigt' : 'vorgang',
      sinnbild: 'currency-eur',
      titel: `${alsEuro(r.bezahlterBetrag)} erhalten`,
      detail: `Zahlungsstand ${ZAHLUNGSSTANDNAME[r.zahlungsstand]}`,
      datum: r.bezahltAm === null ? '—' : kurzTag(r.bezahltAm),
      wann: r.bezahltAm ?? stichtag,
    });
  }

  if (r.zahlungsstand === 'ausgebucht') {
    eintraege.push({
      kennung: 'ausgebucht',
      art: 'still',
      sinnbild: 'folder-open',
      titel: 'Forderung ausgebucht',
      detail: r.ausbuchungsgrund ?? undefined,
      datum: '—',
      wann: stichtag,
    });
  }

  /* RNG-05: Storno und Gutschrift sind eigene Belege mit Bezug auf diesen. */
  for (const k of r.korrekturbelege) {
    eintraege.push({
      kennung: `korrektur-${k.id}`,
      art: 'vorgang',
      sinnbild: 'receipt',
      titel: `${BELEGARTNAME[k.belegart]} ${k.rechnungsnummer ?? '(Entwurf)'}`,
      detail: 'Korrekturbeleg zu dieser Rechnung',
      datum: kurzTag(k.belegdatum),
      wann: k.belegdatum,
      beiWahl: () => beiRechnung(k.id),
    });
  }

  return eintraege
    .sort((a, b) => b.wann.localeCompare(a.wann))
    .map(({ wann: _wann, ...eintrag }) => eintrag);
}

type Aktenreiter = 'positionen' | 'angaben' | 'beleg';

/**
 * Die Rechnungsakte.
 *
 * Seitentyp Akte (ÄA-41): Kopf mit Sinnbild und Kennzahlen, links die Reiter
 * Positionen · Angaben · Beleg, rechts die Seitenleiste mit Status, Summen,
 * Verlauf (Zahlungen und Mahnstand) und dem Zahlungseingang.
 *
 * DIE FEHLENDEN PFLICHTANGABEN STEHEN GANZ OBEN und nicht in einer Fußnote.
 * RNG-02 sagt, dass sie die Festschreibung *verhindern*; eine Meldung, die
 * man erst beim Klicken sieht, wäre die Meldung, die ERR-01 gerade verbietet.
 * Der Knopf bleibt zu, solange etwas fehlt — und darüber steht, was.
 */
export function Rechnungsakte({
  id,
  darfAendern,
  darfBuchen,
  darfStornieren,
  darfVersenden,
  darfLoeschen = false,
  beiKopf,
  beiListe,
  beiRechnung,
  wege,
}: {
  readonly id: string;
  /** CRM-118 (AUF-06): die Wege zu Kunde und Auftrag, nach Recht gesetzt. */
  readonly wege?: Aktenwege;
  /**
   * SYS-03: `rechnung/aendern` — den Entwurf ausfüllen.
   *
   * Ohne dieses Recht steht der Leistungszeitpunkt als Text da, wie am
   * festgeschriebenen Beleg. Ein Feld anzubieten, das beim Speichern mit 403
   * antwortet, ist keine Auskunft, sondern eine Falle (ERR-01).
   */
  readonly darfAendern: boolean;
  readonly darfBuchen: boolean;
  readonly darfStornieren: boolean;
  /** C9.7: Belege erzeugen, freigeben und versenden — rechnung/versenden, wie die API. */
  readonly darfVersenden: boolean;
  /** `rechnung/loeschen` (0006): den Entwurf verwerfen — Auftrag CRM-119, L8. */
  readonly darfLoeschen?: boolean;
  readonly beiKopf: (kopf: Kopf) => void;
  readonly beiListe: () => void;
  readonly beiRechnung: (id: string) => void;
}): JSX.Element {
  const [rechnung, setzeRechnung] = useState<Rechnung | undefined>(undefined);
  const [posten, setzePosten] = useState<OffenerPosten | undefined>(undefined);
  const [mahnliste, setzeMahnliste] = useState<readonly Navisionmahnung[]>([]);
  const [reiter, setzeReiter] = useState<Aktenreiter>('positionen');
  const [stornogrund, setzeStornogrund] = useState('');
  const [betrag, setzeBetrag] = useState('');
  const [eingangAm, setzeEingangAm] = useState(heute());
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  /* Auftrag CRM-103: welche Rückfrage gerade offen ist. */
  const [gutschriftOffen, setzeGutschriftOffen] = useState(false);
  const [rueckfrage, setzeRueckfrage] = useState<
    'festschreiben' | 'stornieren' | 'zahlung' | 'loeschen' | null
  >(null);
  /* KUN-08: das Pop-up zum Wechsel des Rechnungsempfängers. */
  const [empfaengerwahl, setzeEmpfaengerwahl] = useState(false);
  /* Auftrag CRM-119, L8: die Angaben des Entwurfs und die Zeile, die herausgenommen werden soll. */
  const [angabenOffen, setzeAngabenOffen] = useState(false);
  const [entfernen, setzeEntfernen] = useState<string | null>(null);
  const [guthabenstand, setzeGuthabenstand] = useState(0);
  /* ÄA-63: Bearbeiten ab Tablet — auf dem Telefon steht der Entwurf zum Lesen da. */
  const telefon = useTelefon();
  /* Im Ref: `laden` setzt den Kopf und soll nicht bei jedem Bild neu entstehen. */
  const zurListe = useRef(beiListe);
  zurListe.current = beiListe;
  const wegeImKopf = useRef(wege);
  wegeImKopf.current = wege;

  const laden = useCallback(async (): Promise<void> => {
    try {
      const geladen = await holeAkte(id);
      setzeRechnung(geladen);
      setzeBetrag(geladen.summen.belegbrutto);
      const offen = geladen.status === 'festgeschrieben' && OFFEN.includes(geladen.zahlungsstand);
      /*
       * Ein Entwurf hat noch keine Nummer (RNG-02: sie wird beim
       * Festschreiben vergeben). Der Kicker sagt das dann auch so — "Rechnung
       * · Entwurf, Nummer beim Festschreiben" — statt das Wort "Entwurf" an
       * die Stelle zu setzen, an der sonst eine Nummer steht.
       */
      beiKopf({
        kicker:
          geladen.rechnungsnummer === null
            ? `${BELEGARTNAME[geladen.belegart]} · Entwurf, Nummer beim Festschreiben`
            : `${BELEGARTNAME[geladen.belegart]} ${geladen.rechnungsnummer} · ${STATUSNAME[geladen.status]}`,
        titel: geladen.betreff,
        unterzeile: geladen.kundenname,
        sinnbild: 'receipt',
        kennzeichen: (
          <>
            <span className="marke">{BELEGARTNAME[geladen.belegart]}</span>
            <span className={markenklasse(STATUSMARKE[geladen.status])}>
              {STATUSNAME[geladen.status]}
            </span>
          </>
        ),
        kennzahlen: (
          <Kennzahlenleiste
            zahlen={[
              {
                beschriftung: 'Kunde',
                wert: (
                  <Aktenverweis ziel="kunde" id={geladen.kunde} wege={wegeImKopf.current}>
                    {geladen.empfaengerName ?? geladen.kundenname}
                  </Aktenverweis>
                ),
              },
              { beschriftung: 'Belegdatum', wert: alsTag(geladen.belegdatum) ?? '—' },
              {
                beschriftung: 'Fällig am',
                wert: geladen.status === 'entwurf' ? '—' : (alsTag(geladen.faelligAm) ?? '—'),
                frist: offen,
              },
              { beschriftung: 'Rechnungsbetrag', wert: alsEuro(geladen.summen.belegbrutto) },
              {
                beschriftung: 'Erhalten',
                wert: geladen.status === 'entwurf' ? '—' : alsEuro(geladen.bezahlterBetrag),
              },
              {
                beschriftung: 'Zahlung',
                wert: geladen.status === 'entwurf' ? '—' : ZAHLUNGSSTANDNAME[geladen.zahlungsstand],
              },
            ]}
          />
        ),
        handlungen: (
          <button type="button" className="knopf" onClick={() => zurListe.current()}>
            Zur Liste
          </button>
        ),
      });
      setzeFehler(undefined);

      /*
       * DER MAHNSTAND KOMMT AUS DEN OFFENEN POSTEN (RNG-24), gefiltert auf den
       * Kunden. Bleibt die Antwort aus, fehlt nur dieser eine Eintrag im
       * Verlauf; die Akte steht trotzdem.
       */
      /*
       * BP-31, Auftrag CRM-113: Auch die stornierte Rechnung und ihr Storno
       * haben einen Posten, solange ein Guthaben steht — er steht beim
       * Ursprungsbeleg.
       */
      if (geladen.status !== 'entwurf') {
        try {
          const alle = await holeOffenePosten({ kunde: geladen.kunde });
          setzePosten(
            alle.find((p) => p.rechnung === geladen.id || p.rechnung === geladen.bezugsbeleg),
          );
        } catch {
          setzePosten(undefined);
        }
        /* RNG-25: Bleibt die Antwort aus, fehlen nur die Mahnungen im Verlauf. */
        try {
          setzeMahnliste(await holeMahnungen({ rechnung: geladen.id }));
        } catch {
          setzeMahnliste([]);
        }
      } else {
        setzePosten(undefined);
        setzeMahnliste([]);
      }
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [id, beiKopf]);

  useEffect(() => {
    void laden();
  }, [laden]);

  /**
   * Führt einen Vorgang aus und lädt die Akte neu. Eine leere `meldung` heißt:
   * Der Vorgang hat seine Rückmeldung selbst gesetzt („Festgeschrieben als
   * …") — sie wird dann nicht überschrieben.
   */
  async function handle(was: () => Promise<void>, meldung: string): Promise<void> {
    try {
      await bestaetigt(was, meldung);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  /**
   * Wie `handle`, nur WIRFT ein Fehler weiter: Die Rückfrage (Auftrag CRM-103)
   * zeigt ihn in ihrem Pop-up und bleibt offen. Erst der Erfolg schließt sie.
   */
  async function bestaetigt(was: () => Promise<void>, meldung: string): Promise<void> {
    await was();
    setzeRueckfrage(null);
    setzeEntfernen(null);
    if (meldung !== '') setzeRueckmeldung(meldung);
    setzeFehler(undefined);
    await laden();
  }

  if (rechnung === undefined) {
    return (
      <>
        {/* Nach einem Fehler lädt nichts mehr — dann steht nur die Meldung da. */}
        {fehler !== undefined ? <Meldung fehler={fehler} /> : <p role="status">Wird geladen …</p>}
      </>
    );
  }

  const fehlt = rechnung.fehlendePflichtangaben;
  const s = rechnung.summen;
  const verlauf = verlaufDerRechnung(rechnung, posten, heute(), beiRechnung, mahnliste);
  /* Für die Rückfragen: wen der Beleg betrifft und wie er heißt. */
  const empfaenger = rechnung.empfaengerName ?? rechnung.kundenname;
  const nummer = rechnung.rechnungsnummer ?? rechnung.betreff;
  /* SYS-03, RNG-04: nur der Entwurf, nur mit dem Recht — und ab Tablet (ÄA-63). */
  const aenderbar = rechnung.status === 'entwurf' && darfAendern;
  const bearbeitbar = aenderbar && !telefon;
  const zuEntfernen = rechnung.positionen.find((p) => p.id === entfernen);

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {empfaengerwahl && (
        <Empfaengerwahl
          rechnung={rechnung}
          beiSchliessen={() => setzeEmpfaengerwahl(false)}
          beiGewechselt={(meldung) => {
            setzeEmpfaengerwahl(false);
            setzeRueckmeldung(meldung);
            void laden();
          }}
        />
      )}

      {angabenOffen && (
        <Angabenmaske
          rechnung={rechnung}
          beiSchliessen={() => setzeAngabenOffen(false)}
          beiGesichert={(meldung) => {
            setzeAngabenOffen(false);
            setzeRueckmeldung(meldung);
            setzeFehler(undefined);
            void laden();
          }}
        />
      )}

      {/* RNG-02 */}
      {rechnung.status === 'entwurf' && fehlt.length > 0 && (
        <div className="warnung">
          <strong>Pflichtangaben nach § 14 UStG fehlen.</strong> Solange sie fehlen, lässt sich die
          Rechnung nicht festschreiben — sie berechtigte den Empfänger nicht zum
          Vorsteuerabzug, und das fiele bei ihm auf.
          <ul>
            {fehlt.map((was) => (
              <li key={was}>{was}</li>
            ))}
          </ul>
        </div>
      )}

      {/* RNG-04 */}
      {rechnung.status !== 'entwurf' && (
        <p className="hinweiszeile">
          Festgeschrieben am {alsTag(rechnung.festgeschriebenAm) ?? '—'}
          {rechnung.festgeschriebenVonName !== null && ` von ${rechnung.festgeschriebenVonName}`}.
          Änderungen laufen ausschließlich über Storno oder Gutschrift.
        </p>
      )}

      <Aktenrumpf
        haupt={
          <>
            <Reiterleiste
              beschriftung="Rechnung"
              gewaehlt={reiter}
              beiWahl={setzeReiter}
              reiter={[
                { kennung: 'positionen', beschriftung: 'Positionen', zahl: rechnung.positionen.length },
                { kennung: 'angaben', beschriftung: 'Angaben' },
                { kennung: 'beleg', beschriftung: 'Beleg' },
              ]}
            />
            <div role="tabpanel" id={`tafel-${reiter}`} aria-labelledby={`reiter-${reiter}`}>
              {reiter !== 'beleg' && <AbTabletHinweis an={aenderbar} />}
              {reiter === 'positionen' && (
                <Positionen
                  rechnung={rechnung}
                  eingebbar={bearbeitbar}
                  beiZeitraum={(position, von, bis, meldung) =>
                    void handle(
                      () => leistungszeitraumSetzen(rechnung.id, position, von, bis),
                      meldung,
                    )
                  }
                  beiEntfernen={setzeEntfernen}
                />
              )}

              {reiter === 'angaben' && (
                <Angaben
                  rechnung={rechnung}
                  beiRechnung={beiRechnung}
                  beiEmpfaengerWechseln={bearbeitbar ? () => setzeEmpfaengerwahl(true) : undefined}
                  beiBearbeiten={bearbeitbar ? () => setzeAngabenOffen(true) : undefined}
                />
              )}

              {reiter === 'beleg' && (
                <Belegabschnitt
                  belegart="rechnung"
                  beleg={rechnung.id}
                  empfaengerVorschlag={versandadresse(rechnung)}
                  darfErzeugen={darfVersenden}
                  erzeugenGesperrt={
                    rechnung.status === 'entwurf'
                      ? 'Ein Dokument entsteht erst nach der Festschreibung — vorher hat die Rechnung '
                        + 'keine Nummer. Die Vorschau zeigt den Entwurf schon jetzt.'
                      : undefined
                  }
                />
              )}
            </div>
          </>
        }
        seite={
          <>
            <div className="akte__seitentitel">
              <span>Zahlung und Verlauf</span>
            </div>

            <section className="seitenblock" aria-labelledby="rechnung-status">
              <div className="seitenblock__kopf">
                <h2 id="rechnung-status" className="seitenblock__titel">
                  Status
                </h2>
                <span>
                  <span className={markenklasse(STATUSMARKE[rechnung.status])}>
                    {STATUSNAME[rechnung.status]}
                  </span>
                  {rechnung.status !== 'entwurf' && (
                    <span className={markenklasse(ZAHLUNGSMARKE[rechnung.zahlungsstand])}>
                      {ZAHLUNGSSTANDNAME[rechnung.zahlungsstand]}
                    </span>
                  )}
                </span>
              </div>

              {rechnung.status === 'entwurf' && darfBuchen && (
                <div className="knopfreihe">
                  {/* RNG-02: zu, solange eine Pflichtangabe fehlt — sie steht oben. */}
                  <button
                    type="button"
                    className="knopf knopf--haupt"
                    disabled={fehlt.length > 0}
                    onClick={() => setzeRueckfrage('festschreiben')}
                  >
                    Festschreiben …
                  </button>
                  {/* RNG-04 */}
                  <span className="feld__hilfe">Danach ist der Beleg unveränderlich.</span>
                </div>
              )}

              {/* SYS-16, RNG-03: nur der Entwurf — er hat noch keine Nummer, die fehlen könnte. */}
              {rechnung.status === 'entwurf' && darfLoeschen && (
                <div className="knopfreihe">
                  <button
                    type="button"
                    className="knopf knopf--eingriff"
                    onClick={() => setzeRueckfrage('loeschen')}
                  >
                    Entwurf löschen …
                  </button>
                </div>
              )}

              {/* RNG-05, C9.1: storniert werden Rechnungen, keine Stornos und Gutschriften. */}
              {rechnung.status === 'festgeschrieben'
                && rechnung.belegart !== 'storno'
                && rechnung.belegart !== 'gutschrift'
                && darfStornieren && (
                <>
                  <div className="feld">
                    <label className="feld__beschriftung" htmlFor="rg-storno">
                      Grund des Stornos
                    </label>
                    <input
                      id="rg-storno"
                      type="text"
                      value={stornogrund}
                      onChange={(e) => setzeStornogrund(e.target.value)}
                    />
                  </div>
                  <div className="knopfreihe">
                    <button
                      type="button"
                      className="knopf knopf--eingriff"
                      disabled={stornogrund.trim() === ''}
                      onClick={() => setzeRueckfrage('stornieren')}
                    >
                      Stornieren …
                    </button>
                  </div>
                  {/* RNG-05 */}
                  <p className="feld__hilfe">
                    Der Storno ist ein <strong>eigener Beleg</strong> mit Bezug auf diesen; dieser
                    bleibt vollständig erhalten und einsehbar.
                  </p>
                  {/* RNG-11, BP-31: die Teilkorrektur neben dem Storno — Positionen oder Betrag. */}
                  <div className="knopfreihe">
                    <button type="button" className="knopf" onClick={() => setzeGutschriftOffen(true)}>
                      Gutschrift …
                    </button>
                  </div>
                </>
              )}
            </section>

            <section className="seitenblock" aria-labelledby="rechnung-summen">
              <h2 id="rechnung-summen" className="seitenblock__titel">
                Summen
              </h2>
              {/* RND-05, RND-07: je Steuersatzgruppe, nicht je Position. */}
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle summentafel">
                  <thead>
                    <tr>
                      <th scope="col">Steuersatz</th>
                      <th scope="col" className="zahl">
                        Netto
                      </th>
                      <th scope="col" className="zahl">
                        Steuer
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.gruppen.map((g) => (
                      <tr key={g.schluessel}>
                        <td>
                          {alsProzent(g.prozent)} USt
                          {g.hinweis !== null && <span className="kanalliste__name"> · {g.hinweis}</span>}
                        </td>
                        <td className="zahl">{alsEuro(g.netto)}</td>
                        <td className="zahl">{alsEuro(g.steuer)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row">Nettobetrag</th>
                      <td className="zahl">{alsEuro(s.belegnetto)}</td>
                      <td className="zahl">{alsEuro(s.steuerSumme)}</td>
                    </tr>
                    <tr>
                      <th scope="row">Rechnungsbetrag</th>
                      <td className="zahl" colSpan={2}>
                        <strong>{alsEuro(s.belegbrutto)}</strong>
                      </td>
                    </tr>
                    {/* PRF-17: Anrechnung auf den Zahlbetrag, nicht auf Netto oder Steuer. */}
                    {rechnung.zuschuesse.map((z) => (
                      <tr key={z.gutschein}>
                        <th scope="row">abzüglich {z.bezeichnung}</th>
                        <td className="zahl" colSpan={2}>
                          −{alsEuro(z.betrag)}
                        </td>
                      </tr>
                    ))}
                    {/* CRM-174, PRF-17: Wertgutscheine sind Zahlung — sie mindern nur den Zahlbetrag. */}
                    {rechnung.wertgutscheine.map((w) => (
                      <tr key={w.gutschein}>
                        <th scope="row">abzüglich Wertgutschein {w.nummer}</th>
                        <td className="zahl" colSpan={2}>
                          −{alsEuro(w.betrag)}
                        </td>
                      </tr>
                    ))}
                    {(rechnung.zuschuesse.length > 0 || rechnung.wertgutscheine.length > 0) && (
                      <tr>
                        <th scope="row">Zahlbetrag</th>
                        <td className="zahl" colSpan={2}>
                          <strong>{alsEuro(rechnung.zahlbetrag)}</strong>
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
              {rechnung.status === 'entwurf' && rechnung.zuschuesse.length > 0 && (
                <p className="hinweiszeile">
                  Die Gutscheine werden mit der Festschreibung angerechnet.
                </p>
              )}
              {/* CRM-169: nicht still — ein Gutschein, den die Rechnung nicht mehr aufnimmt. */}
              {rechnung.zuschuesseNichtAngerechnet.length > 0 && (
                <p className="warnung" role="status">
                  Nicht angerechnet:{' '}
                  {rechnung.zuschuesseNichtAngerechnet
                    .map((z) => `${z.bezeichnung} (${alsEuro(z.betrag)})`)
                    .join('; ')}
                  . Die Gutscheine passen auf Zeilen dieser Rechnung, wurden aber erst nach der
                  Festschreibung erfasst. Eine festgeschriebene Rechnung ändert sich nicht mehr:
                  Stornieren Sie sie und erstellen Sie sie neu, oder verwerfen Sie die Gutscheine.
                </p>
              )}
              <VormerkungenOhneNamen offen={rechnung.vormerkungenOffen} />
              <Rundungshinweis faelle={s.nichtDarstellbar} />
            </section>

            {/* RNG-23, RNG-24 */}
            <Verlauf
              titel="Verlauf"
              zusatz={
                rechnung.status === 'entwurf' ? undefined : ZAHLUNGSSTANDNAME[rechnung.zahlungsstand]
              }
              eintraege={verlauf}
              leer="Noch kein Vorgang."
            />

            {rechnung.status === 'festgeschrieben' && darfBuchen && (
              <section className="seitenblock" aria-labelledby="rechnung-zahlung">
                <h2 id="rechnung-zahlung" className="seitenblock__titel">
                  Zahlungseingang
                </h2>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="rg-betrag">
                    Bisher insgesamt erhalten
                  </label>
                  <input
                    id="rg-betrag"
                    className="zahl"
                    type="text"
                    inputMode="decimal"
                    value={betrag}
                    onChange={(e) => setzeBetrag(e.target.value)}
                  />
                  {/* RNG-23, C18.4: Der Stand folgt dem Betrag — teilbezahlt oder bezahlt. */}
                  <span className="feld__hilfe">
                    Die Summe aller Eingänge. Ob die Rechnung damit teilbezahlt oder bezahlt ist,
                    ergibt sich aus dem Betrag; Skonto innerhalb der Frist wird berücksichtigt.
                  </span>
                </div>
                <div className="feld">
                  <label className="feld__beschriftung" htmlFor="rg-eingang">
                    Eingegangen am
                  </label>
                  <input
                    id="rg-eingang"
                    type="date"
                    value={eingangAm}
                    onChange={(e) => setzeEingangAm(e.target.value)}
                  />
                </div>
                <div className="knopfreihe">
                  <button
                    type="button"
                    className="knopf"
                    onClick={() => setzeRueckfrage('zahlung')}
                  >
                    Zahlungseingang vermerken …
                  </button>
                </div>
                {/* FIB-08 */}
                <p className="feld__hilfe">
                  Bis zur Navision-Anbindung wird der Zahlungseingang hier vermerkt; danach fließt
                  er zurück.
                </p>
              </section>
            )}
            {/* DAT-12, BUC-145 (AeA-64): Guthaben erstatten oder mit einer anderen Forderung verrechnen. */}
            {posten !== undefined &&
              (posten.art === 'guthaben' ||
                Number(posten.erstattet) > 0 ||
                Number(posten.verrechnetAus) > 0) && (
                <section className="seitenblock" aria-labelledby="rechnung-guthaben">
                  <h2 id="rechnung-guthaben" className="seitenblock__titel">
                    Guthaben
                  </h2>
                  {posten.art === 'guthaben' && (
                    <p>
                      {`Der Kunde hat ${alsEuro(posten.offenerBetrag.replace('-', ''))} gut. Es wird erstattet oder mit einer anderen offenen Forderung desselben Kunden verrechnet.`}
                    </p>
                  )}
                  <Guthabenverlauf posten={posten.rechnung} stand={guthabenstand} />
                  {posten.art === 'guthaben' && darfBuchen && (
                    <div className="knopfreihe">
                      <Guthabenhandlung
                        posten={posten.rechnung}
                        rechnungsnummer={rechnung.rechnungsnummer}
                        beiErledigt={(meldung) => {
                          setzeRueckmeldung(meldung);
                          setzeGuthabenstand((n) => n + 1);
                          void laden();
                        }}
                      />
                    </div>
                  )}
                </section>
              )}
            <Rechnungsverbunden rechnung={rechnung} wege={wege} beiRechnung={beiRechnung} />
          </>
        }
      />

      {rueckfrage === 'festschreiben' && (
        <Rueckfrage
          frage={`${BELEGARTNAME[rechnung.belegart]} festschreiben?`}
          unterzeile={`${rechnung.betreff} · ${empfaenger}`}
          tat="Festschreiben"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={() =>
            bestaetigt(async () => {
              const neu = await festschreiben(rechnung.id);
              setzeRueckmeldung(`Festgeschrieben als ${neu.rechnungsnummer}.`);
            }, '')
          }
        >
          {/* RNG-04 */}
          <p>
            Der Beleg über <strong>{alsEuro(s.belegbrutto)}</strong> an {empfaenger} erhält
            seine Rechnungsnummer und ist danach unveränderlich. Das lässt sich nicht
            zurücknehmen — Änderungen laufen dann nur noch über Storno oder Gutschrift.
          </p>
          {/* RNG-11, BP-31: was die festgeschriebene Gutschrift im offenen Posten bewirkt. */}
          {rechnung.belegart === 'gutschrift' && (
            <p>
              {rechnung.bezugsnummer !== null
                ? `Die Gutschrift mindert danach den offenen Posten von ${rechnung.bezugsnummer}.`
                : 'Die Gutschrift steht danach als Guthaben des Kunden, bis es erstattet oder verrechnet ist.'}
            </p>
          )}
          <VormerkungenOhneNamen offen={rechnung.vormerkungenOffen} />
        </Rueckfrage>
      )}

      {rueckfrage === 'loeschen' && (
        <Rueckfrage
          frage="Rechnungsentwurf löschen?"
          unterzeile={`${rechnung.betreff} · ${empfaenger}`}
          tat="Löschen"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={async () => {
            await loeschen(rechnung.id);
            setzeRueckfrage(null);
            beiListe();
          }}
        >
          {/* SYS-16, AUF-20: Die Mengen waren noch nicht berechnet — sie bleiben am Auftrag offen. */}
          <p>
            Der Entwurf über <strong>{alsEuro(s.belegbrutto)}</strong> verschwindet aus der Liste.
            Was er aus dem Auftrag übernommen hat, bleibt dort offen und lässt sich erneut
            berechnen. Eine Rechnungsnummer ist noch nicht vergeben, die Nummernreihe bleibt
            lückenlos.
          </p>
        </Rueckfrage>
      )}

      {zuEntfernen !== undefined && (
        <Rueckfrage
          frage="Position aus dem Entwurf nehmen?"
          unterzeile={`${zuEntfernen.bezeichnung} · ${alsEuro(zuEntfernen.netto)}`}
          tat="Herausnehmen"
          beiSchliessen={() => setzeEntfernen(null)}
          beiBestaetigen={() =>
            bestaetigt(
              () => positionLoeschen(rechnung.id, zuEntfernen.id),
              `Position „${zuEntfernen.bezeichnung}“ aus dem Entwurf genommen.`,
            )
          }
        >
          {/* AUF-01, AUF-20: Der Vertrag bleibt; die Menge steht am Auftrag weiter offen. */}
          <p>
            Die Zeile verschwindet aus dieser Rechnung. Am Auftrag bleibt sie offen und erscheint
            wieder in der Fakturierungsliste.
          </p>
        </Rueckfrage>
      )}

      {gutschriftOffen && (
        <GutschriftZurRechnung
          rechnung={rechnung}
          beiSchliessen={() => setzeGutschriftOffen(false)}
          beiAngelegt={(neu) => {
            setzeGutschriftOffen(false);
            beiRechnung(neu);
          }}
        />
      )}

      {rueckfrage === 'stornieren' && (
        <Rueckfrage
          frage={`${BELEGARTNAME[rechnung.belegart]} stornieren?`}
          unterzeile={`${nummer} · ${empfaenger}`}
          tat="Stornieren"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={() =>
            bestaetigt(async () => {
              const neu = await stornieren(rechnung.id, stornogrund);
              setzeRueckmeldung(`Storno ${neu.rechnungsnummer} erzeugt.`);
              beiRechnung(neu.id);
            }, '')
          }
        >
          {/* RNG-05: Der Grund steht im Formular dahinter und wird hier nur gezeigt. */}
          <p>
            Zu {nummer} über <strong>{alsEuro(s.belegbrutto)}</strong> entsteht ein Storno als
            eigener Beleg mit eigener Nummer. Die Rechnung selbst bleibt vollständig erhalten und
            einsehbar.
          </p>
          <p>Grund: „{stornogrund.trim()}“</p>
        </Rueckfrage>
      )}

      {rueckfrage === 'zahlung' && (
        <Rueckfrage
          frage="Zahlungseingang vermerken?"
          unterzeile={`${nummer} · ${empfaenger}`}
          tat="Vermerken"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={() =>
            bestaetigt(
              () =>
                zahlungVermerken(rechnung.id, {
                  zahlungsstand: 'bezahlt',
                  bezahlterBetrag: betrag.replace(',', '.'),
                  bezahltAm: eingangAm === '' ? null : eingangAm,
                }),
              'Zahlungseingang vermerkt; der Zahlungsstand steht im Verlauf.',
            )
          }
        >
          {/* RNG-23: Der Vermerk führt die Summe aller Eingänge, nicht die einzelne Zahlung. */}
          <p>
            Bisher insgesamt erhalten: <strong>{alsEuro(betrag.replace(',', '.'))}</strong>
            {eingangAm === '' ? ', ohne Eingangsdatum' : ` am ${alsTag(eingangAm) ?? eingangAm}`}
            {' '}— bei einem Rechnungsbetrag von {alsEuro(s.belegbrutto)}.
          </p>
          <p>
            Der Betrag ersetzt den bisher vermerkten Stand von {alsEuro(rechnung.bezahlterBetrag)}.
          </p>
        </Rueckfrage>
      )}
    </>
  );
}

/** Der Reiter „Positionen": die Zeilen des Belegs. */
function Positionen({
  rechnung,
  eingebbar,
  beiZeitraum,
  beiEntfernen,
}: {
  readonly rechnung: Rechnung;
  /** Leistungszeitpunkt eingebbar: Entwurf UND `rechnung/aendern` (SYS-03, RNG-04). */
  readonly eingebbar: boolean;
  readonly beiZeitraum: (
    position: string,
    von: string | null,
    bis: string | null,
    meldung: string,
  ) => void;
  /** Auftrag CRM-119: eine Zeile herausnehmen — die Rückfrage stellt die Akte. */
  readonly beiEntfernen: (position: string) => void;
}): JSX.Element {
  return (
    <div className="tafel--weit">
      <table className="liste__tabelle">
        <caption className="nur-vorlesen">Positionen</caption>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Leistung</th>
            <th scope="col">Leistungszeitpunkt</th>
            <th scope="col" className="zahl">
              Menge
            </th>
            <th scope="col" className="zahl">
              Einzelpreis
            </th>
            <th scope="col">Steuer</th>
            <th scope="col" className="zahl">
              Netto
            </th>
            {eingebbar && (
              <th scope="col">
                <span className="nur-vorlesen">Handlung</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rechnung.positionen.map((p, i) => (
            <tr key={p.id}>
              <td className="zahl">{i + 1}</td>
              <td>
                {p.bezeichnung}
                {/* RNG-09 */}
                {p.art === 'anzahlungsabzug' && (
                  <span className={markenklasse('stillgelegt')}>Abzug</span>
                )}
              </td>
              <td>
                {eingebbar ? (
                  /*
                   * EINGEBBAR, SOLANGE ES EIN ENTWURF IST
                   * (§ 14 Abs. 4 Nr. 6).
                   *
                   * Der Regelfall ist die Ableitung aus dem Termin
                   * (0086) — dann steht das Datum schon hier. Ohne
                   * Termin gäbe es sonst KEINEN Weg: Am 04.09.2026
                   * standen 19 Entwürfe im Betrieb, von denen sich
                   * keiner festschreiben ließ.
                   *
                   * Zwei Felder, weil § 31 Abs. 4 UStDV auch einen
                   * Zeitraum zulässt. Das zweite bleibt leer, wenn
                   * alles an einem Tag geschah.
                   */
                  <span className="zeitraum">
                    <input
                      type="date"
                      aria-label={`Leistungsbeginn Position ${i + 1}`}
                      defaultValue={p.leistungVon ?? ''}
                      onBlur={(e) => {
                        if ((e.target.value || null) === p.leistungVon) return;
                        beiZeitraum(
                          p.id,
                          e.target.value || null,
                          p.leistungBis,
                          'Leistungszeitpunkt gespeichert.',
                        );
                      }}
                    />
                    <input
                      type="date"
                      aria-label={`Leistungsende Position ${i + 1}`}
                      defaultValue={p.leistungBis ?? ''}
                      disabled={p.leistungVon === null}
                      onBlur={(e) => {
                        if ((e.target.value || null) === p.leistungBis) return;
                        beiZeitraum(
                          p.id,
                          p.leistungVon,
                          e.target.value || null,
                          'Leistungszeitraum gespeichert.',
                        );
                      }}
                    />
                  </span>
                ) : p.leistungVon === null ? (
                  // BP-33: Er entscheidet über die Periode; fehlt er,
                  // steht es oben in den Pflichtangaben.
                  <span className="marke marke--gesperrt">fehlt</span>
                ) : p.leistungBis === null ? (
                  p.leistungVon
                ) : (
                  `${p.leistungVon} – ${p.leistungBis}`
                )}
              </td>
              <td className="zahl">{alsMenge(p.menge)}</td>
              <td className="zahl">{alsEuro(p.einzelpreis)}</td>
              <td>{p.steuerschluessel ?? '—'}</td>
              <td className="zahl">{alsEuro(p.netto)}</td>
              {eingebbar && (
                <td>
                  {/*
                    * AUF-01, AUF-16: Preise und Mengen sind der Vertrag und ändern
                    * sich am Auftrag. Am Entwurf lässt sich eine Zeile nur
                    * herausnehmen — der Abzug einer Anzahlung (RNG-09) ebenso.
                    */}
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    aria-label={`Position ${i + 1} herausnehmen`}
                    onClick={() => beiEntfernen(p.id)}
                  >
                    Herausnehmen …
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Der Reiter „Angaben": Empfänger und Zahlungsbedingungen. Die Rechnung
 * entsteht aus dem Auftrag; am Entwurf lassen sich Texte und Bedingungen im
 * Pop-up „Angaben bearbeiten“ anpassen (Auftrag CRM-119), festgeschrieben
 * ändert sich an ihr nichts mehr (RNG-04).
 */
function Angaben({
  rechnung,
  beiRechnung,
  beiEmpfaengerWechseln,
  beiBearbeiten,
}: {
  readonly rechnung: Rechnung;
  readonly beiRechnung: (id: string) => void;
  /** KUN-08: nur am Entwurf und mit dem Recht zum Ändern gesetzt. */
  readonly beiEmpfaengerWechseln?: (() => void) | undefined;
  /** Auftrag CRM-119: Texte und Zahlungsbedingungen des Entwurfs — gesetzt wie oben. */
  readonly beiBearbeiten?: (() => void) | undefined;
}): JSX.Element {
  const bezug = rechnung.bezugsbeleg;
  return (
    <>
      {beiBearbeiten !== undefined && (
        <div className="knopfreihe">
          <button type="button" className="knopf" onClick={beiBearbeiten}>
            Angaben bearbeiten …
          </button>
        </div>
      )}
      <Feldraster>
        <Feldzeile beschriftung="Betreff" wert={rechnung.betreff} />
        {/* RNG-07, RNG-23 */}
        <Feldzeile beschriftung="Kunde" wert={rechnung.empfaengerName ?? rechnung.kundenname} />
        <Feldzeile
          beschriftung="Anschrift"
          wert={rechnung.empfaengerAnschrift}
          anzeige={
            rechnung.empfaengerAnschrift === null ? (
              <span className="marke marke--gesperrt">fehlt (§ 14 Abs. 4 Nr. 1)</span>
            ) : (
              <span className="anschrift">{rechnung.empfaengerAnschrift}</span>
            )
          }
        />
        {/*
          * KUN-08, BUC-103: der Ansprechpartner, an den die Rechnung geht —
          * eingefroren an der Rechnung, im Entwurf wechselbar.
          */}
        <Feldzeile
          beschriftung="Rechnungsempfänger"
          wert={rechnung.empfaengerKontaktName}
          anzeige={
            <>
              {rechnung.empfaengerKontaktName === null
                ? 'das Unternehmen, ohne „z. Hd.“'
                : `z. Hd. ${rechnung.empfaengerKontaktName}`}
              {versandadresse(rechnung) !== null && ` · E-Mail an ${versandadresse(rechnung) ?? ''}`}
              {beiEmpfaengerWechseln !== undefined && (
                <>
                  {' '}
                  <button type="button" className="knopf knopf--klein" onClick={beiEmpfaengerWechseln}>
                    Wechseln …
                  </button>
                </>
              )}
            </>
          }
        />
        <Feldzeile beschriftung="Belegdatum" wert={alsTag(rechnung.belegdatum)} />
        <Feldzeile
          beschriftung="Fällig am"
          wert={alsTag(rechnung.faelligAm)}
          anzeige={`${alsTag(rechnung.faelligAm) ?? '—'} · ${rechnung.zahlungsziel} Tage`}
        />
        {rechnung.skontoProzent !== null && (
          <Feldzeile
            beschriftung="Skonto"
            wert={rechnung.skontoProzent}
            anzeige={`${alsProzent(rechnung.skontoProzent)} bis ${alsTag(rechnung.skontoBis) ?? '—'}`}
          />
        )}
        <Feldzeile
          beschriftung="Zahlung"
          wert={ZAHLUNGSSTANDNAME[rechnung.zahlungsstand]}
          anzeige={
            <>
              {ZAHLUNGSSTANDNAME[rechnung.zahlungsstand]}
              {Number(rechnung.bezahlterBetrag) !== 0 && (
                <>
                  {' · '}
                  <span className="zahl">{alsEuro(rechnung.bezahlterBetrag)}</span> erhalten
                </>
              )}
            </>
          }
        />
        {rechnung.bestellreferenz !== null && (
          <Feldzeile beschriftung="Bestellreferenz" wert={rechnung.bestellreferenz} />
        )}
        {rechnung.leitwegId !== null && <Feldzeile beschriftung="Leitweg-ID" wert={rechnung.leitwegId} />}
        {bezug !== null && rechnung.bezugsnummer !== null && (
          <Feldzeile
            beschriftung="Bezug"
            wert={rechnung.bezugsnummer}
            anzeige={
              <button type="button" className="liste__oeffnen" onClick={() => beiRechnung(bezug)}>
                {rechnung.bezugsnummer}
              </button>
            }
          />
        )}
        {rechnung.kopftext !== null && <Feldzeile beschriftung="Kopftext" wert={rechnung.kopftext} />}
        {rechnung.fusstext !== null && <Feldzeile beschriftung="Fußtext" wert={rechnung.fusstext} />}
      </Feldraster>
    </>
  );
}

/** RNG-25: die letzte Mahnstufe aus Navision, kurz für Liste und Akte. */
export function mahnstandText(
  p: Pick<OffenerPosten, 'mahnstufe' | 'mahndatum'>,
): string {
  if (p.mahnstufe === null || p.mahndatum === null) return '—';
  return `${p.mahnstufe}. Mahnung am ${alsTag(p.mahndatum)}`;
}

/** RNG-25: die Mahngebühren als Kurzinfo; ohne Mahnung keine. */
export function mahngebuehrText(p: Pick<OffenerPosten, 'mahnstufe' | 'mahngebuehr'>): string | undefined {
  if (p.mahnstufe === null || p.mahngebuehr === null) return undefined;
  return `Mahngebühren ${alsEuro(p.mahngebuehr)} (in Navision gebucht)`;
}

/**
 * Die Offene-Posten-Liste (RNG-24).
 *
 * Seitentyp Liste (ÄA-41): Zähler im Seitenkopf, die Fälligkeitsstaffel als
 * Segmente in der Filterleiste — je Stufe mit ihrer Zahl —, die Posten als
 * Rastertabelle.
 *
 * DIE STAFFEL KOMMT VOM SERVER. AUS-01 verlangt, dass zwei Auswertungen
 * derselben Kennzahl nie zwei Zahlen liefern — eine Grenze in TypeScript wäre
 * die zweite. Die Segmente filtern nur, was der Server geliefert hat.
 */
export function OffenePosten({
  zurKunde,
  beiRechnung,
  darfBuchen,
}: {
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde?: ((id: string) => void) | undefined;
  readonly beiRechnung: (id: string) => void;
  /** DAT-12: Guthaben erstatten und verrechnen — das Recht des Zahlungsvermerks. */
  readonly darfBuchen: boolean;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly OffenerPosten[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [staffel, setzeStaffel] = useState<string>('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [geladen, setzeGeladen] = useState(0);

  useEffect(() => {
    void holeOffenePosten()
      .then(setzeZeilen)
      .catch((u: unknown) => setzeFehler(u as ApiFehler))
      .finally(() => setzeLaedt(false));
  }, [geladen]);

  const mitGuthaben = darfBuchen && zeilen.some((z) => z.art === 'guthaben');

  const summeJeStaffel = new Map<string, number>();
  for (const z of zeilen) {
    summeJeStaffel.set(z.staffel, (summeJeStaffel.get(z.staffel) ?? 0) + 1);
  }
  const gezeigt = staffel === '' ? zeilen : zeilen.filter((z) => z.staffel === staffel);

  useZaehler(laedt ? undefined : zaehlwort(zeilen.length, 'offener Posten', 'offene Posten'));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      <Liste
        filter={
          summeJeStaffel.size > 0 ? (
            <Segmente
              beschriftung="Fälligkeitsstaffel"
              gewaehlt={staffel}
              beiWahl={setzeStaffel}
              optionen={[
                { wert: '', text: `alle · ${zeilen.length}` },
                ...[...summeJeStaffel].map(([name, anzahl]) => ({
                  wert: name,
                  text: `${name} · ${anzahl}`,
                })),
              ]}
            />
          ) : undefined
        }
        hinweise={
          <p className="hilfe">
            Festgeschriebene Rechnungen, die noch offen sind. Storno und Gutschrift gleichen
            ihre Rechnung aus; ein negativer Betrag ist ein Guthaben des Kunden, bis es
            erstattet oder mit einer anderen Forderung verrechnet ist. Die Fälligkeitsstaffel kommt vom Server: Zwei Auswertungen derselben
            Kennzahl müssen dieselben Grenzen ziehen. Gemahnt wird in Navision; die Spalte
            „Mahnung“ zeigt die letzte Stufe aus dem täglichen Abgleich.
          </p>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">{gezeigt.length} offene Posten</caption>
            <thead>
              <tr>
                <th scope="col" className="liste__spalte--knapp">
                  Nummer
                </th>
                <th scope="col">Kunde</th>
                <th scope="col">Belegdatum</th>
                <th scope="col">fällig</th>
                <th scope="col" className="zahl">
                  Tage
                </th>
                <th scope="col">Staffel</th>
                <th scope="col">Mahnung</th>
                <th scope="col">Zahlung</th>
                <th scope="col" className="zahl">
                  Erhalten
                </th>
                <th scope="col" className="zahl">
                  Offen
                </th>
                {mitGuthaben && (
                  <th scope="col">
                    <span className="nur-vorlesen">Guthaben</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {gezeigt.map((z) => (
                <tr key={z.rechnung}>
                  <td className="liste__spalte--knapp">
                    <button
                      type="button"
                      className="liste__oeffnen liste__nummer"
                      onClick={() => beiRechnung(z.rechnung)}
                    >
                      {z.rechnungsnummer}
                    </button>
                  </td>
                  <td className="liste__name">
                  <KundeVerweis kundeId={z.kunde} zurAkte={zurKunde}>
                    {z.kundenname}
                  </KundeVerweis>
                </td>
                  <td className="liste__einzeilig">{alsTag(z.belegdatum)}</td>
                  <td className="liste__einzeilig">{alsTag(z.faelligAm)}</td>
                  <td className="zahl">{z.tageUeberfaellig > 0 ? z.tageUeberfaellig : '—'}</td>
                  <td>
                    <span
                      className={markenklasse(
                        z.staffel === 'nicht faellig'
                          ? 'stillgelegt'
                          : z.art === 'guthaben'
                            ? 'aufmerksamkeit'
                            : 'sackgasse',
                      )}
                    >
                      {z.staffel}
                    </span>
                  </td>
                  <td className="liste__einzeilig" title={mahngebuehrText(z)}>
                    {mahnstandText(z)}
                  </td>
                  <td>
                    <span className={markenklasse(ZAHLUNGSMARKE[z.zahlungsstand])}>
                      {ZAHLUNGSSTANDNAME[z.zahlungsstand]}
                    </span>
                  </td>
                  <td className="zahl">{alsEuro(z.bezahlterBetrag)}</td>
                  <td className="zahl">
                    {z.art === 'guthaben' ? (
                      <span title={z.ausgleich === null ? undefined : `ausgeglichen durch ${z.ausgleich}`}>
                        {alsEuro(z.offenerBetrag)}
                      </span>
                    ) : (
                      alsEuro(z.offenerBetrag)
                    )}
                  </td>
                  {mitGuthaben && (
                    <td className="liste__einzeilig">
                      {z.art === 'guthaben' && (
                        <div className="knopfreihe">
                          <Guthabenhandlung
                            posten={z.rechnung}
                            rechnungsnummer={z.rechnungsnummer}
                            beiErledigt={(meldung) => {
                              setzeRueckmeldung(meldung);
                              setzeGeladen((n) => n + 1);
                            }}
                          />
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!laedt && zeilen.length === 0 && (
          <div className="liste__leer" role="status">
            <p>Kein offener Posten — alles ist bezahlt.</p>
          </div>
        )}
      </Liste>
    </>
  );
}

/**
 * DIE BELEGKETTE DER RECHNUNG (CRM-118, L7; AUF-06, RNG-05, RNG-11): Kunde
 * und Aufträge nach oben, Bezugsbeleg und Korrekturbelege daneben. Der Weg
 * zu einem anderen Beleg ist `beiRechnung` und hängt am selben Recht wie
 * diese Akte.
 */
function Rechnungsverbunden({
  rechnung,
  wege,
  beiRechnung,
}: {
  readonly rechnung: Rechnung;
  readonly wege: Aktenwege | undefined;
  readonly beiRechnung: (id: string) => void;
}): JSX.Element {
  const belegwege: Aktenwege = { ...wege, rechnung: beiRechnung };
  return (
    <Verbunden
      zeilen={[
        {
          beschriftung: 'Kunde',
          eintraege: [
            <Aktenverweis key="k" ziel="kunde" id={rechnung.kunde} wege={wege}>
              {rechnung.kundenname}
            </Aktenverweis>,
          ],
        },
        {
          beschriftung: 'Aufträge',
          eintraege: rechnung.auftraege.map((a) => (
            <Aktenverweis key={a.id} ziel="auftrag" id={a.id} wege={wege}>
              {a.auftragsnummer}
            </Aktenverweis>
          )),
        },
        ...(rechnung.bezugsbeleg === null
          ? []
          : [
              {
                beschriftung: 'Bezug',
                eintraege: [
                  <Aktenverweis key="b" ziel="rechnung" id={rechnung.bezugsbeleg} wege={belegwege}>
                    {rechnung.bezugsnummer ?? 'Bezugsbeleg'}
                  </Aktenverweis>,
                ],
              },
            ]),
        {
          beschriftung: 'Korrekturen',
          eintraege: rechnung.korrekturbelege.map((k) => (
            <Aktenverweis key={k.id} ziel="rechnung" id={k.id} wege={belegwege}>
              {BELEGARTNAME[k.belegart]} {k.rechnungsnummer ?? `(Entwurf vom ${alsTag(k.belegdatum) ?? k.belegdatum})`}
            </Aktenverweis>
          )),
        },
      ]}
    />
  );
}

/**
 * AeA-71 (CRM-191): vorgemerkte Gutscheine, zu denen noch kein Gutschein mit
 * Nummer und Name erfasst ist. Ein Hinweis, keine Sperre: Angerechnet wird nur
 * Benanntes (PRF-17), die Rechnung selbst ändert sich nicht.
 */
function VormerkungenOhneNamen({ offen }: { readonly offen: Rechnung['vormerkungenOffen'] }): JSX.Element | null {
  if (offen.length === 0) return null;
  const auftraege = [...new Set(offen.map((v) => v.auftragsnummer))];
  return (
    <p className="warnung" role="status">
      {offen
        .map((v) => `${v.offen} ${v.offen === 1 ? 'Gutschein' : 'Gutscheine'} ${v.kostentraegerName} zu ${alsEuro(v.betrag)}`)
        .join('; ')}{' '}
      vorgemerkt, noch ohne Namen – {offen.length === 1 && offen[0]!.offen === 1 ? 'wird' : 'werden'} nicht angerechnet.
      Tragen Sie Nummer und Name in der Auftragsakte {auftraege.join(', ')} unter „Gutscheine“ ein, dann rechnet
      die Festschreibung sie an. Kommt niemand mit Gutschein, lösen Sie die Vormerkung dort auf.
    </p>
  );
}
